import cx from 'classnames';
import { useState } from 'react';

import { AnnouncementCode } from '../frontend-libs/evlapp-types';

import { Button, Icon, Text } from '../common/components';
import { IconNames } from '../common/components/Icon/IconNames';
import { useAppSelector, useAppThunkDispatch } from '../common/hooks';
import { t } from '../common/i18n';
import { dismissAnnouncement, selectAnnouncements } from '../common/store/announcementSlice';

/**
 * Ikony toast oznámení. Kódy bez záznamu se jako toast nevykreslují
 * (např. onboarding-units-arrow žije jen v Sidebaru).
 */
const BANNER_ICONS: Partial<Record<AnnouncementCode, IconNames>> = {
  'unit-multiple-owners': 'users',
  'garage-with-quorum': 'garage',
};

/**
 * In-app oznámení pro adminy jako plovoucí toast bubliny v pravém horním rohu —
 * překrývají obsah (neodsouvají ho), sbalené ukazují titulek + 2 řádky textu,
 * rozbalují se najetím kurzoru nebo klepnutím (dotyková zařízení).
 * Zmizí tlačítkem „Beru na vědomí".
 */
export const AnnouncementBanners: React.FC = () => {
  const items = useAppSelector(selectAnnouncements).items;
  const dispatch = useAppThunkDispatch();
  const [expandedCodes, setExpandedCodes] = useState<AnnouncementCode[]>([]);

  const banners = items.filter((item) => BANNER_ICONS[item.code] !== undefined);
  if (banners.length === 0) return null;

  const toggleExpanded = (code: AnnouncementCode) => {
    setExpandedCodes((codes) => (codes.includes(code) ? codes.filter((item) => item !== code) : [...codes, code]));
  };

  return (
    <div className="AnnouncementToasts">
      {banners.map((item) => {
        const namespace = `Announcements.Banner.${item.code}`;

        return (
          <div
            className={cx('AnnouncementToast', expandedCodes.includes(item.code) && 'AnnouncementToast--expanded')}
            key={item.code}
            role="button"
            tabIndex={0}
            aria-expanded={expandedCodes.includes(item.code)}
            onClick={() => toggleExpanded(item.code)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toggleExpanded(item.code);
              }
            }}
          >
            <div className="AnnouncementToast__icon">
              <Icon name={BANNER_ICONS[item.code] as IconNames} />
            </div>
            <div className="AnnouncementToast__body">
              <Text as="div" size="body-medium" weight="600" color="Neutral800">
                {t('title', namespace)}
              </Text>
              <Text as="div" size="body-small" color="Neutral700" className="AnnouncementToast__text">
                {item.count !== null && <>{t('intro', namespace, { count: item.count })} </>}
                {t('text', namespace)}
              </Text>
              {/* Rozbalovací část — plynulá výška přes grid-template-rows 0fr → 1fr */}
              <div className="AnnouncementToast__more">
                <div className="AnnouncementToast__more-inner">
                  <Text as="div" size="body-small" color="Neutral700" className="AnnouncementToast__hint">
                    {t('hint', namespace)}
                  </Text>
                  <div className="AnnouncementToast__action">
                    <Button
                      size="small"
                      variant="secondary"
                      title={t('Announcements.acknowledge')}
                      onClick={() => dispatch(dismissAnnouncement(item.code))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
